/* ── Shared types for the Standards App frontend ─────────────── */

/* ── Corpus types (C1) ──────────────────────────────────────── */

export interface CorpusStandard {
  standard: string;
  edition: string;
  clause_count: number;
  last_updated: string;
  verification_status: string;
}

export interface CorpusSkill {
  skill: string;
  standards: CorpusStandard[];
}

export interface CorpusData {
  skills: CorpusSkill[];
}

/* ── Chat types ─────────────────────────────────────────────── */

export interface Citation {
  /** Backend-assigned clause identifier, e.g. "as3000-4.3" */
  id: string;
  /** 1-based display number matching the inline [N] badge */
  number: number;
  /** Human-readable standard name, e.g. "AS/NZS 3000:2018" */
  standard: string;
  /** Clause reference, e.g. "4.3.1" */
  clause: string;
  /** Full clause text (may be long) */
  text: string;
  /** Optional skill tag, e.g. "electrical-compliance" */
  skill?: string;
  /** Edition year, e.g. "2018" — shown when not already in the standard name */
  edition?: string;
  /** Provenance string e.g. "HTML" — indicates zero transcription error risk */
  provenance?: string | null;
}

export interface UsageStats {
  inputTokens: number;
  outputTokens: number;
  model: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations: Citation[];
  isStreaming: boolean;
  error?: string;
  /** Query ID from SSE — used for feedback (C4) */
  queryId?: string;
  /** LLM usage stats from the usage SSE event */
  usage?: UsageStats;
  /** Elapsed milliseconds for the full response (send → done) */
  elapsed?: number;
  /** Follow-up suggestion strings parsed from FOLLOW_UPS block */
  followUps?: string[];
  /** Confidence level from backend analysis */
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'INSUFFICIENT';
  /** Explanation of confidence assessment */
  confidenceReason?: string;
  /** Original query text (for confidence badge feedback) */
  queryText?: string;
  /** Structured checklist data (when checklist mode is used) */
  checklist?: ChecklistData;
  /** Corpus version active when this answer was generated (for staleness detection) */
  corpusVersion?: string | null;
  /** True if the corpus has been updated since this answer was generated */
  corpusStale?: boolean;
  /** G4: True if this response was served from the query cache */
  cached?: boolean;
  /** Retrieval Map tree data from the retrieval_tree SSE event */
  retrievalTree?: RetrievalTree | null;
}

/* ── Phase 6 types ──────────────────────────────────────────── */

/** Page image info from clause-pages endpoint */
export interface PageInfo {
  pdf_id: string;
  page_start: number;
  page_end: number | null;
  has_image: boolean;
  /** Signed HMAC URL issued by handleClausePages — expires 15min. Use this instead of getPageImageUrl(). */
  image_url?: string | null;
}

/* ── Bookmark types ──────────────────────────────────────────── */

export interface Bookmark {
  id: string;
  query: string;
  response_preview: string;
  citations: string[];
  query_id: string;
  created_at: string;
}

/* ── Query search result (full-text search across sessions) ── */

export interface QuerySearchResult {
  query_id: string;
  query_text: string;
  response_preview: string;
  standard_refs: string[];
  session_id: string | null;
  created_at: string;
}

/** Issue submission payload */
export interface IssueSubmission {
  category: 'wrong_answer' | 'missing_info' | 'wrong_citation' | 'outdated' | 'app_bug' | 'standard_request' | 'other';
  severity: 'minor' | 'important' | 'critical';
  description: string;
  query_id?: string;
  query_text?: string;
  response_text?: string;
}

/* ── Checklist types ────────────────────────────────────────── */

export type ItemStatus = 'unchecked' | 'pass' | 'fail' | 'na';

export type ItemTag = 'mandatory' | 'guidance';

/** Photo evidence record attached to a checklist item */
export interface ChecklistEvidence {
  id: string;
  item_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  caption: string;
  created_at: string;
  /** URL to fetch the image — requires auth header */
  url: string;
}

export interface ChecklistItem {
  /** D1 item UUID — present when loaded/saved from server */
  id?: string;
  text: string;
  standard: string;
  clause: string;
  /** @deprecated Use `status` instead */
  checked: boolean;
  /** Tri-state assessment status */
  status: ItemStatus;
  /** Per-item notes / observations */
  notes: string;
  /** Mandatory vs guidance tag parsed from item text */
  tag?: ItemTag;
  /** Photo evidence attached to this item (lazy-loaded from server) */
  evidence?: ChecklistEvidence[];
}

export interface ChecklistCategory {
  name: string;
  items: ChecklistItem[];
}

export interface ChecklistCoverage {
  domains: number;
  totalDomains: number;
  clausesMatched: number;
  raw?: string;
}

export interface ChecklistData {
  /** D1 checklist UUID — present when saved/loaded from server */
  checklistId?: string;
  title: string;
  categories: ChecklistCategory[];
  /** Coverage metadata parsed from SSE <!-- COVERAGE: ... --> comment */
  coverage?: ChecklistCoverage;
  /** Coverage summary text */
  coverageSummary?: string;
  /** Raw markdown content for fallback rendering when parsing fails */
  rawMarkdown?: string;
  // F3: Signed/Locked lifecycle
  signedBy?: string;
  signedAt?: string;
  signedComment?: string;
  locked?: boolean;
  voidReason?: string;
  voidBy?: string;
  voidAt?: string;
  // Signature fields
  inspectorSignature?: string;
  inspectorName?: string;
  inspectorSignedAt?: string;
  reviewerSignature?: string;
  reviewerName?: string;
  reviewerSignedAt?: string;
}

/** F3: Checklist audit log entry */
export interface ChecklistAuditEntry {
  id: string;
  checklist_id: string;
  action: 'signed' | 'voided';
  actor_user_id?: string;
  actor_name?: string;
  detail?: string;
  created_at: string;
}

/* ── FigureInfo ─────────────────────────────────────────────── */

export interface FigureInfo {
  figure_id: string;
  severity: 'normative' | 'warning' | 'info';
  caption?: string;
  pages_view_url?: string;
  page?: number;
}

/* ── Phase 8: User Preferences ──────────────────────────────── */

export type Jurisdiction = 'AU' | 'NZ' | 'both' | 'none';

export interface UserPreferences {
  default_jurisdiction: Jurisdiction;
  default_domains: string[];
  plain_english: boolean;
  theme: string;
  show_usage: boolean;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  default_jurisdiction: 'none',
  default_domains: [],
  plain_english: false,
  theme: 'dark',
  show_usage: false,
};

/** Available domain/skill slugs for filtering */
export const DOMAIN_OPTIONS: { slug: string; label: string }[] = [
  { slug: 'electrical-compliance', label: 'Electrical Safety' },
  { slug: 'ncc-compliance', label: 'NCC / Building' },
  { slug: 'plumbing-compliance', label: 'Plumbing' },
  { slug: 'eess-registration', label: 'EESS Registration' },
  { slug: 'emc-compliance', label: 'EMC / RF' },
  { slug: 'product-safety', label: 'Product Safety' },
  { slug: 'acl-consumer-law', label: 'Consumer Law (ACL)' },
  { slug: 'fire-safety', label: 'Fire Safety' },
  { slug: 'accessibility-compliance', label: 'Accessibility' },
  { slug: 'energy-efficiency', label: 'Energy Efficiency' },
  { slug: 'waterproofing-compliance', label: 'Waterproofing' },
  { slug: 'glazing-compliance', label: 'Glazing' },
  { slug: 'structural-compliance', label: 'Structural' },
];

export const JURISDICTION_OPTIONS: { value: Jurisdiction; label: string }[] = [
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'both', label: 'Both' },
  { value: 'none', label: 'None (all)' },
];

/* ── Research Brief types ───────────────────────────────────── */

export type BriefSectionStatus = 'queued' | 'searching' | 'writing' | 'complete' | 'error';

export interface BriefSection {
  id: number;
  title: string;
  query: string;
  content: string;
  citations: Citation[];
  isComplete: boolean;
  isStreaming: boolean;
  status: BriefSectionStatus;
}

export interface BriefMeta {
  title: string;
  briefId: string;
  generatedAt: string;
  totalCitations: number;
  sectionCount: number;
  queryText: string;
}

export interface BriefProgress {
  phase: 'decomposing' | 'researching' | 'synthesising' | 'complete';
  sections: {
    id: number;
    title: string;
    status: BriefSectionStatus;
    citationCount: number;
  }[];
  overallPercent: number;
  statusText?: string;
}

/* ── Side-by-Side Comparison types ──────────────────────────── */

export interface JurisdictionTarget {
  id: string;
  label: string;
  group: string;
}

export interface JurisdictionGroup {
  name: string;
  icon: string;
  targets: JurisdictionTarget[];
}

export type SbsJurisdictionStatus = 'queued' | 'searching' | 'streaming' | 'complete' | 'error';

export interface SbsJurisdictionResult {
  jurisdictionId: string;
  label: string;
  status: SbsJurisdictionStatus;
  content: string;
  citations: Citation[];
  confidence?: string;
  confidenceReason?: string;
  error?: string;
}

export interface SbsComparisonState {
  comparisonId: string | null;
  query: string;
  selectedJurisdictions: string[];
  results: Map<string, SbsJurisdictionResult>;
  synthesis: string;
  synthesisStatus: 'idle' | 'generating' | 'complete';
  synthesisConfidence?: string;
  error: string | null;
}

export const JURISDICTION_GROUPS: JurisdictionGroup[] = [
  {
    name: 'Building Code', icon: '🏗️',
    targets: [
      { id: 'ncc-2025', label: 'NCC 2025', group: 'Building Code' },
      { id: 'ncc-2022', label: 'NCC 2022 (Amdt 2)', group: 'Building Code' },
      { id: 'nz-building-code', label: 'NZ Building Code', group: 'Building Code' },
      { id: 'uk-building-regs', label: 'UK Building Regs', group: 'Building Code' },
    ],
  },
  {
    name: 'Electrical', icon: '⚡',
    targets: [
      { id: 'asnzs-3000', label: 'AS/NZS 3000:2018', group: 'Electrical' },
      { id: 'electrical-vic', label: 'VIC Electrical', group: 'Electrical' },
      { id: 'electrical-nsw', label: 'NSW Electrical', group: 'Electrical' },
      { id: 'electrical-qld', label: 'QLD Electrical', group: 'Electrical' },
      { id: 'electrical-wa', label: 'WA Electrical', group: 'Electrical' },
      { id: 'electrical-sa', label: 'SA Electrical', group: 'Electrical' },
      { id: 'electrical-tas', label: 'TAS Electrical', group: 'Electrical' },
      { id: 'electrical-act', label: 'ACT Electrical', group: 'Electrical' },
      { id: 'electrical-nt', label: 'NT Electrical', group: 'Electrical' },
      { id: 'electrical-nz', label: 'NZ Electrical', group: 'Electrical' },
      { id: 'eu-electrical', label: 'EU Electrical', group: 'Electrical' },
    ],
  },
  {
    name: 'Plumbing', icon: '🚿',
    targets: [
      { id: 'asnzs-3500', label: 'AS/NZS 3500 (2025)', group: 'Plumbing' },
      { id: 'ncc-plumbing', label: 'NCC Vol 3 Plumbing', group: 'Plumbing' },
      { id: 'watermark', label: 'WaterMark Scheme', group: 'Plumbing' },
    ],
  },
  {
    name: 'Accessibility', icon: '♿',
    targets: [
      { id: 'as-1428', label: 'AS 1428.1:2021', group: 'Accessibility' },
    ],
  },
  {
    name: 'Product Compliance', icon: '✅',
    targets: [
      { id: 'eess-aunz', label: 'EESS (AU/NZ)', group: 'Product Compliance' },
      { id: 'eu-ce-marking', label: 'EU CE Marking', group: 'Product Compliance' },
      { id: 'uk-ukca', label: 'UK UKCA', group: 'Product Compliance' },
    ],
  },
  {
    name: 'Consumer Law', icon: '⚖️',
    targets: [
      { id: 'acl-federal', label: 'Australian Consumer Law', group: 'Consumer Law' },
    ],
  },
];

/* ── Retrieval Map types ─────────────────────────────────────── */

export interface RetrievalTreeDomain {
  id: string;
  label: string;
  search_order: number;
  candidate_count: number;
  selected_count: number;
}

export interface RetrievalTreeClause {
  id: string;
  domain: string;
  standard: string;
  clause_number: string;
  title: string;
  rank: number;
  rrf_score: number;
  rerank_score: number | null;
  retrieval_source: 'vector' | 'fts' | 'forced' | 'hybrid' | 'cross_ref_only';
  cited: boolean;
}

export interface RetrievalTreeMeta {
  reranked: boolean;
  rerank_latency_ms?: number | null;
  vector_topk?: number;
  candidate_pool?: number;
  final_count?: number;
}

export interface RetrievalTree {
  schema: 1;
  query_id?: string;
  query: string;
  scope?: { applied: boolean; domains: string[] };
  domains: RetrievalTreeDomain[];
  clauses: RetrievalTreeClause[];
  cross_refs: Array<{ from_clause_id: string; to_clause_id: string; ref_type: 'normative' | 'informative' }>;
  not_retrieved: Array<{
    id: string | null;
    standard: string;
    clause_number?: string;
    domain: string;
    /** Clause title — populated for cross_ref_only ghost nodes */
    title?: string;
    reason: 'not_in_corpus' | 'below_threshold' | 'topk_limit' | 'domain_filtered' | 'cross_ref_only';
    rrf_score?: number;
    rerank_score?: number;
    /** IDs of retrieved clauses that reference this ghost node */
    referenced_by?: string[];
  }>;
  meta: RetrievalTreeMeta;
}

/* ── Compliance Copy Builder types ──────────────────────────────── */

export const MANUAL_PRODUCT_CATEGORIES = [
  'Tap ware / Faucets',
  'Shower fittings',
  'Hot water system',
  'Toilet / WC',
  'Basin / Sink',
  'Light switch / Socket',
  'Luminaire / Light fitting',
  'Electrical appliance',
  'Power outlet / GPO',
  'Safety switch / RCD',
  'Window / Glazing',
  'Door / Door hardware',
  'Insulation / Sarking',
  'Balustrade / Handrail',
  'Waterproofing membrane',
  'Structural fastener',
  'Accessibility fitting',
  'Other (describe below)',
] as const;

export const MANUAL_TARGET_REGIONS = [
  { id: 'AU', label: 'Australia' },
  { id: 'NZ', label: 'New Zealand' },
  { id: 'UK/EU', label: 'UK / EU' },
] as const;

export interface ManualSectionCitation {
  standard: string;
  clause: string;
  text: string;
}

export interface ManualSection {
  section_id: string;
  title: string;
  body: string;
  citations: ManualSectionCitation[];
  normative_status: 'mandatory' | 'recommended';
}

export type ManualBuilderPhase =
  | 'idle'
  | 'clarifying'
  | 'classified'
  | 'generating'
  | 'complete'
  | 'error';

export interface ManualBuilderState {
  phase: ManualBuilderPhase;
  productType: string;
  targetRegions: string[];
  productDetails: string;
  clarificationQuestions: string[];
  clarificationAnswers: Record<string, string>;
  productName: string;
  domains: string[];
  corpusHits: number;
  sections: ManualSection[];
  error: string | null;
}

// ─── Manual Review Types ─────────────────────────────────────────────────────

export type ReviewGrade = 'PASS' | 'PARTIAL' | 'FAIL' | 'NA';

export interface ReviewGap {
  requirement: string;
  source_standard: string;
  source_clause: string;
  domain: string;
  normative_status: string;
  grade: ReviewGrade;
  manual_excerpt: string | null;
  recommendation: string | null;
}

export type ManualReviewPhase =
  | 'idle'
  | 'uploading'
  | 'identifying'
  | 'confirmation_needed'
  | 'sweeping'
  | 'grading'
  | 'complete'
  | 'error';

export interface ManualReviewState {
  phase: ManualReviewPhase;
  /** Name of the uploaded PDF file */
  fileName: string;
  productName: string;
  manufacturer: string | null;
  model: string | null;
  domains: string[];
  gaps: ReviewGap[];
  /** Total requirements count */
  total: number;
  pass: number;
  partial: number;
  fail: number;
  na: number;
  complianceScore: number;
  /** Ambiguous product guesses (confirmation_needed phase) */
  guesses: string[];
  confirmedProductType: string;
  error: string | null;
}

export const REVIEW_GRADE_LABELS: Record<ReviewGrade, string> = {
  PASS: 'Pass',
  PARTIAL: 'Partial',
  FAIL: 'Fail',
  NA: 'N/A',
};

export const REVIEW_GRADE_COLORS: Record<ReviewGrade, string> = {
  PASS: '#22c55e',
  PARTIAL: '#f59e0b',
  FAIL: '#ef4444',
  NA: '#94a3b8',
};

export const MANUAL_SECTION_LABELS: Record<string, string> = {
  product_classification: 'Product Classification',
  warnings_and_hazards: 'Safety Warnings and Hazards',
  safety_requirements: 'Safety Requirements',
  installation_instructions: 'Installation Instructions',
  compliance_markings: 'Compliance Markings and Certifications',
  consumer_law_notices: 'Consumer Law Notices',
  liability_statements: 'Liability Statements',
  recommended_additions: 'Recommended Additions',
};

export interface ResearchBriefState {
  isActive: boolean;
  briefId: string | null;
  meta: BriefMeta | null;
  sections: BriefSection[];
  executiveSummary: string;
  progress: BriefProgress | null;
  error: string | null;
}

/* ── G2: Query Tab State ─────────────────────────────────────── */

export interface TabState {
  id: string;
  /** First ~40 chars of first user message, or "New Query" */
  label: string;
  messages: ChatMessage[];
  sessionId: string | null;
  activeQueryId: string | null;
  // Brief state
  briefSections: BriefSection[];
  briefExecutiveSummary: string;
  briefProgress: BriefProgress | null;
  briefMeta: BriefMeta | null;
  briefError: string | null;
  briefActive: boolean;
  // SBS state
  sbsState: SbsComparisonState | null;
  sbsActive: boolean;
  // Streaming indicator (never persisted as true)
  isStreaming: boolean;
  /** Retrieval Map view preference for this tab */
  citationPanelView?: 'sources' | 'map';
  /** Latest retrieval tree for the active query in this tab */
  retrievalTree?: RetrievalTree | null;
}
